import { z } from 'zod';

import type { AgentTestScenario, ScenarioBody } from './scenario';
import { AGENT_TEST_SCOPES, AGENT_TEST_TURN_ROLES } from './scenario';

const scenarioTurnFormSchema = z.object({
  role: z.enum(AGENT_TEST_TURN_ROLES),
  content: z.string().min(1, 'El contenido del turno es requerido'),
  toolName: z.string().optional(),
  toolCallId: z.string().optional(),
});

export const scenarioFormSchema = z
  .object({
    name: z.string().min(1, 'El nombre es requerido').max(255, 'Máximo 255 caracteres'),
    description: z.string().max(1000, 'Máximo 1000 caracteres'),
    scope: z.enum(AGENT_TEST_SCOPES),
    subProjectId: z.number().int().positive().nullable(),
    requiredTools: z.array(z.string()),
    turns: z.array(scenarioTurnFormSchema).min(1, 'Agrega al menos un turno'),
    expectedBehavior: z.string().min(1, 'El comportamiento esperado es requerido'),
    category: z
      .string()
      .max(255, 'Máximo 255 caracteres')
      .refine((value) => value.trim().length > 0, 'La categoría es requerida'),
    toolStubsText: z.string(),
    enabled: z.boolean(),
  })
  .superRefine((data, ctx) => {
    if (data.scope === 'subProject' && data.subProjectId === null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['subProjectId'],
        message: 'Selecciona el proyecto para este alcance',
      });
    }

    const userTurns = data.turns.filter((turn) => turn.role === 'user').length;
    if (userTurns < 1) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['turns'],
        message: 'Agrega al menos un turno de rol "Cliente"',
      });
    }

    const lastTurn = data.turns[data.turns.length - 1];
    if (lastTurn && lastTurn.role !== 'user') {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['turns'],
        message: 'El último turno debe ser un mensaje del Cliente (el agente responde a partir de él)',
      });
    }

    if (data.toolStubsText.trim().length > 0) {
      try {
        const parsed: unknown = JSON.parse(data.toolStubsText);
        if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['toolStubsText'],
            message: 'Los tool stubs deben ser un objeto JSON (por tool)',
          });
        }
      } catch {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['toolStubsText'],
          message: 'JSON inválido',
        });
      }
    }
  });

export type ScenarioFormValues = z.infer<typeof scenarioFormSchema>;

export const emptyScenarioFormValues = (): ScenarioFormValues => ({
  name: '',
  description: '',
  scope: 'common',
  subProjectId: null,
  requiredTools: [],
  turns: [{ role: 'user', content: '' }],
  expectedBehavior: '',
  category: '',
  toolStubsText: '',
  enabled: true,
});

export const scenarioFormValuesFromScenario = (scenario: AgentTestScenario): ScenarioFormValues => ({
  name: scenario.name,
  description: scenario.description ?? '',
  scope: scenario.scope,
  subProjectId: scenario.subProjectId,
  requiredTools: scenario.requiredTools ?? [],
  turns: scenario.turns.map((turn) => ({
    role: turn.role,
    content: turn.content,
    toolName: turn.toolName ?? '',
    toolCallId: turn.toolCallId ?? '',
  })),
  expectedBehavior: scenario.expectedBehavior,
  category: scenario.tag,
  toolStubsText: scenario.toolStubs ? JSON.stringify(scenario.toolStubs, null, 2) : '',
  enabled: scenario.enabled,
});

const parseToolStubs = (toolStubsText: string): Record<string, unknown> | null => {
  if (toolStubsText.trim().length === 0) {
    return null;
  }
  return JSON.parse(toolStubsText) as Record<string, unknown>;
};

export const scenarioBodyFromFormValues = (values: ScenarioFormValues, masterProjectId: number): ScenarioBody => ({
  name: values.name,
  description: values.description.trim(),
  scope: values.scope,
  projectId: values.scope === 'global' ? null : masterProjectId,
  subProjectId: values.scope === 'subProject' ? values.subProjectId : null,
  interactionType: values.turns.filter((turn) => turn.role === 'user').length === 1 ? 'single_turn' : 'multi_turn',
  turns: values.turns.map((turn) => ({
    role: turn.role,
    content: turn.content,
    ...(turn.role === 'tool' && turn.toolName ? { toolName: turn.toolName } : {}),
    ...(turn.role === 'tool' && turn.toolCallId ? { toolCallId: turn.toolCallId } : {}),
  })),
  expectedBehavior: values.expectedBehavior,
  toolStubs: parseToolStubs(values.toolStubsText),
  requiredTools: values.scope === 'global' ? values.requiredTools : [],
  tag: values.category.trim(),
  enabled: values.enabled,
});
